// src/tabs/ImportacaoFinanceiraSubTab.js
import React, { useState, useMemo } from "react";
import { Upload, FileSpreadsheet, ArrowRight, Check, AlertCircle, RefreshCw } from "lucide-react";
import * as XLSX from "xlsx";
import { importacaoFinanceiraService } from "../services/importacaoFinanceiraService";
import { parseCentavos, formatCentavos } from "../utils/moneyUtils";

export default function ImportacaoFinanceiraSubTab({ finance }) {
  const [file, setFile] = useState(null);
  const [sheetData, setSheetData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [lotesRecentes, setLotesRecentes] = useState([]);
  
  // Mapping States
  const [mapping, setMapping] = useState({
    data: "",
    descricao: "",
    valor: "",
    categoria: "",
    centro_custo: "",
  });

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Mapping & Preview, 3: Success

  // Carregar lotes recentes ao montar ou mudar etapa
  const loadRecentImports = React.useCallback(async () => {
    try {
      const res = await importacaoFinanceiraService.getImportacoesRecentes();
      setLotesRecentes(res || []);
    } catch (err) {
      console.warn("Não foi possível carregar lotes de importação recentes:", err.message);
    }
  }, []);

  React.useEffect(() => {
    loadRecentImports();
  }, [step, loadRecentImports]);

  // Executa o parse do arquivo Excel/CSV
  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (json.length === 0) {
          alert("A planilha está vazia!");
          setLoading(false);
          return;
        }

        // Pega a primeira linha como cabeçalho
        const rawHeaders = json[0].map((h) => String(h || "").trim());
        setHeaders(rawHeaders);

        // Converte as outras linhas em objetos associando-as ao cabeçalho
        const rows = [];
        for (let i = 1; i < json.length; i++) {
          const rowData = json[i];
          // Ignora linhas vazias
          if (rowData.length === 0 || rowData.every((val) => val === undefined || val === null || val === "")) {
            continue;
          }
          const rowObj = {};
          rawHeaders.forEach((header, idx) => {
            rowObj[header] = rowData[idx] !== undefined ? rowData[idx] : "";
          });
          rows.push(rowObj);
        }

        setSheetData(rows);

        // Auto sugestão de cabeçalho
        const newMapping = { data: "", descricao: "", valor: "", categoria: "", centro_custo: "" };
        rawHeaders.forEach((h) => {
          const low = h.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          if (low === "data" || low.includes("emissao") || low.includes("vencimento")) newMapping.data = h;
          if (low === "descricao" || low.includes("historico") || low.includes("detalhe")) newMapping.descricao = h;
          if (low === "valor" || low === "total" || low.includes("quantia") || low.includes("preço")) newMapping.valor = h;
          if (low === "categoria" || low.includes("classificacao")) newMapping.categoria = h;
          if (low === "centro de custo" || low.includes("centro") || low.includes("custo")) newMapping.centro_custo = h;
        });

        setMapping(newMapping);
        setStep(2);
      } catch (err) {
        alert("Erro ao ler arquivo Excel: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(uploadedFile);
  };

  // Pré-visualização de 20 linhas parseadas com mapeamento dinâmico
  const previewRows = useMemo(() => {
    if (sheetData.length === 0) return [];

    return sheetData.slice(0, 20).map((row, index) => {
      const rawDataVal = row[mapping.data];
      const rawDescVal = row[mapping.descricao];
      const rawValorVal = row[mapping.valor];
      const rawCatVal = row[mapping.categoria];
      const rawCentroVal = row[mapping.centro_custo];

      // Tenta normalizar a data
      let formattedDate = "";
      if (rawDataVal) {
        try {
          if (typeof rawDataVal === "number") {
            // Excel Serial Date converter
            const dateObj = XLSX.SSF.parse_date_code(rawDataVal);
            const d = new Date(dateObj.y, dateObj.m - 1, dateObj.d);
            formattedDate = d.toISOString().split("T")[0];
          } else {
            const cleanD = String(rawDataVal).replace(/\//g, "-").trim();
            if (cleanD.match(/^\d{4}-\d{2}-\d{2}/)) {
              formattedDate = cleanD.split(" ")[0];
            } else if (cleanD.match(/^\d{2}-\d{2}-\d{4}/)) {
              const parts = cleanD.split("-");
              formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            } else {
              formattedDate = new Date(cleanD).toISOString().split("T")[0];
            }
          }
        } catch (e) {
          formattedDate = "Inválida";
        }
      }

      // Procura Categoria Sugerida no banco
      const matchedCat = finance.categorias.find(
        (c) => c.nome.trim().toUpperCase() === String(rawCatVal || "").trim().toUpperCase()
      );

      // Procura Centro de Custo no banco
      const matchedCentro = finance.centrosCusto.find(
        (c) => c.nome.trim().toUpperCase() === String(rawCentroVal || "").trim().toUpperCase()
      );

      const parsedCents = parseCentavos(rawValorVal);

      return {
        id: index,
        rawRow: row,
        date: formattedDate,
        descricao: rawDescVal ? String(rawDescVal).trim() : "Sem descrição",
        valor_centavos: parsedCents,
        categoria_nome: rawCatVal ? String(rawCatVal).trim().toUpperCase() : "",
        categoria_id: matchedCat?.id || null,
        centro_nome: rawCentroVal ? String(rawCentroVal).trim().toUpperCase() : "",
        centro_id: matchedCentro?.id || null,
        hasError: !formattedDate || formattedDate === "Inválida" || parsedCents === 0,
      };
    });
  }, [sheetData, mapping, finance.categorias, finance.centrosCusto]);

  // Envia todas as linhas válidas para staging (fin_lancamentos_pendentes)
  const handleIngestImport = async () => {
    if (!mapping.data || !mapping.descricao || !mapping.valor) {
      alert("Por favor, selecione as colunas de Data, Descrição e Valor.");
      return;
    }

    setLoading(true);
    try {
      // 1. Criar Lote de Importação
      const importacaoLote = await importacaoFinanceiraService.createLoteImportacao(
        {
          origem_tipo: "upload_arquivo",
          origem_referencia: file.name,
          mapeamento_usado: mapping,
          total_linhas: sheetData.length,
        },
        finance.user
      );

      // 2. Mapeia e Valida TODAS as linhas da planilha
      const pendencias = [];

      sheetData.forEach((row, idx) => {
        const rawDate = row[mapping.data];
        const rawDesc = row[mapping.descricao];
        const rawValor = row[mapping.valor];
        const rawCat = row[mapping.categoria];
        const rawCentro = row[mapping.centro_custo];

        // Parse Data
        let isoDate = "";
        try {
          if (typeof rawDate === "number") {
            const dateObj = XLSX.SSF.parse_date_code(rawDate);
            isoDate = new Date(dateObj.y, dateObj.m - 1, dateObj.d).toISOString().split("T")[0];
          } else {
            const cleanD = String(rawDate).replace(/\//g, "-").trim();
            if (cleanD.match(/^\d{4}-\d{2}-\d{2}/)) {
              isoDate = cleanD.split(" ")[0];
            } else if (cleanD.match(/^\d{2}-\d{2}-\d{4}/)) {
              const parts = cleanD.split("-");
              isoDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            } else {
              isoDate = new Date(cleanD).toISOString().split("T")[0];
            }
          }
        } catch (e) {
          isoDate = new Date().toISOString().split("T")[0]; // Fallback
        }

        const cents = parseCentavos(rawValor);
        const tipoPendencia = cents >= 0 ? "entrada" : "estorno";

        // Procura Categorias e Centros na taxonomia pré-carregada
        const matchedCat = finance.categorias.find(
          (c) => c.nome.trim().toUpperCase() === String(rawCat || "").trim().toUpperCase()
        );
        const matchedCentro = finance.centrosCusto.find(
          (c) => c.nome.trim().toUpperCase() === String(rawCentro || "").trim().toUpperCase()
        );

        // Prepara snapshot JSON conforme I4
        const snapshot = {
          linha_planilha: idx + 2, // 1-based, +1 para cabeçalho
          data_original: rawDate,
          descricao_original: rawDesc,
          valor_original: rawValor,
          categoria_original: rawCat,
          centro_original: rawCentro,
        };

        pendencias.push({
          origem: "importacao_planilha",
          origem_id: `${importacaoLote.id}_line_${idx + 2}`,
          tipo_pendencia: tipoPendencia,
          snapshot: snapshot,
          categoria_sugerida: matchedCat?.id || null,
          centro_custo_sugerido: matchedCentro?.id || null,
          valor_sugerido_centavos: cents,
          data_sugerida: isoDate,
          descricao_sugerida: rawDesc ? String(rawDesc).trim() : `Importação Lote ${importacaoLote.id}`,
        });
      });

      // 3. Cadastra em blocos (chunk size = 10) no staging de pendências
      await importacaoFinanceiraService.createPendenciasBulk(pendencias, importacaoLote.id);

      // 4. Concluir Lote
      await importacaoFinanceiraService.finalizeLoteImportacao(importacaoLote.id);
      
      await finance.refreshPendingCount();
      setStep(3);
    } catch (err) {
      let msg = err.message;
      if (err.response?.data) {
        const details = Object.entries(err.response.data)
          .map(([k, v]) => `${k}: ${v.message || JSON.stringify(v)}`)
          .join("\n");
        msg += "\n\nDetalhes:\n" + details;
      }
      alert("Erro ao realizar carga de importação: " + msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col p-4 lg:p-6 overflow-y-auto bg-slate-50">
      
      {/* ─── PASSO 1: UPLOAD DO ARQUIVO ─────────────────────────────────── */}
      {step === 1 && (
        <div className="max-w-4xl mx-auto w-full grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
          
          {/* Caixa de Upload */}
          <div className="md:col-span-2 bg-white border-2 border-dashed border-slate-300 rounded-xl p-8 flex flex-col items-center justify-center text-center shadow-sm">
            <Upload className="text-slate-400 mb-4 animate-bounce" size={44} />
            <h3 className="font-bold text-sm text-slate-700 uppercase tracking-wide">Importador de Extratos e Despesas</h3>
            <p className="text-xs text-slate-500 max-w-sm mt-1 mb-6">
              Arraste ou selecione uma planilha Excel (<code>.xlsx</code>, <code>.xls</code>) ou <code>.csv</code> contendo a listagem financeira.
            </p>

            <label className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-4 py-2.5 rounded-lg shadow-md cursor-pointer transition-colors">
              Selecionar Planilha
              <input
                type="file"
                accept=".xlsx, .xls, .csv"
                onChange={handleFileUpload}
                className="hidden"
                disabled={loading}
              />
            </label>
            
            {loading && (
              <div className="flex items-center gap-2 mt-4 text-xs font-bold text-blue-600">
                <RefreshCw size={16} className="animate-spin" /> Processando arquivo...
              </div>
            )}
          </div>

          {/* Histórico Recente */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col shadow-sm">
            <h4 className="font-bold text-xs text-slate-400 uppercase tracking-wider mb-3">Lotes Recentes</h4>
            <div className="flex-1 space-y-2 overflow-y-auto max-h-[300px]">
              {lotesRecentes.map((l) => (
                <div key={l.id} className="p-3 bg-slate-50 border border-slate-100 rounded-lg text-[10px]">
                  <div className="font-bold text-slate-700 truncate">{l.origem_referencia}</div>
                  <div className="text-slate-400 mt-1 flex justify-between">
                    <span>Linhas: {l.total_linhas}</span>
                    <span className="font-bold text-emerald-700">Conf: {l.total_confirmadas}</span>
                  </div>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider mt-2 ${
                    l.status === "concluida" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                  }`}>
                    {l.status === "concluida" ? "Finalizado" : "Aguardando Conf."}
                  </span>
                </div>
              ))}
              {lotesRecentes.length === 0 && (
                <span className="text-xs text-slate-400 text-center block py-10">Nenhum lote importado anteriormente.</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── PASSO 2: MAPEAMENTO E PRÉVIA ───────────────────────────────── */}
      {step === 2 && (
        <div className="flex-1 flex flex-col gap-6 max-w-6xl mx-auto w-full">
          
          {/* Seletor Mapeamento */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm shrink-0">
            <h3 className="font-bold text-sm text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <FileSpreadsheet className="text-blue-600" size={18} /> Mapeamento de Colunas
            </h3>
            
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Coluna Data *</label>
                <select
                  value={mapping.data}
                  onChange={(e) => setMapping({ ...mapping, data: e.target.value })}
                  className="w-full text-xs font-semibold p-2 border border-slate-350 rounded bg-white outline-none"
                >
                  <option value="">Selecione...</option>
                  {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Coluna Descrição *</label>
                <select
                  value={mapping.descricao}
                  onChange={(e) => setMapping({ ...mapping, descricao: e.target.value })}
                  className="w-full text-xs font-semibold p-2 border border-slate-350 rounded bg-white outline-none"
                >
                  <option value="">Selecione...</option>
                  {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Coluna Valor *</label>
                <select
                  value={mapping.valor}
                  onChange={(e) => setMapping({ ...mapping, valor: e.target.value })}
                  className="w-full text-xs font-semibold p-2 border border-slate-350 rounded bg-white outline-none"
                >
                  <option value="">Selecione...</option>
                  {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Coluna Categoria</label>
                <select
                  value={mapping.categoria}
                  onChange={(e) => setMapping({ ...mapping, categoria: e.target.value })}
                  className="w-full text-xs font-semibold p-2 border border-slate-350 rounded bg-white outline-none"
                >
                  <option value="">Nenhuma...</option>
                  {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Coluna Centro Custo</label>
                <select
                  value={mapping.centro_custo}
                  onChange={(e) => setMapping({ ...mapping, centro_custo: e.target.value })}
                  className="w-full text-xs font-semibold p-2 border border-slate-350 rounded bg-white outline-none"
                >
                  <option value="">Nenhum...</option>
                  {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Pré-visualização das 20 Linhas */}
          <div className="flex-1 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm flex flex-col min-h-[300px]">
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 shrink-0">
              <h4 className="font-bold text-xs text-slate-400 uppercase tracking-wider">Prévia dos Dados (Primeiras 20 Linhas)</h4>
            </div>

            <div className="flex-1 overflow-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-100/50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                    <th className="px-4 py-2.5">Linha</th>
                    <th className="px-4 py-2.5">Data</th>
                    <th className="px-4 py-2.5">Descrição</th>
                    <th className="px-4 py-2.5">Sugestão Cat</th>
                    <th className="px-4 py-2.5">Sugestão Centro</th>
                    <th className="px-4 py-2.5">Valor Sugerido</th>
                    <th className="px-4 py-2.5">Status Validação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {previewRows.map((row) => (
                    <tr key={row.id} className={`hover:bg-slate-50/50 ${row.hasError ? "bg-red-50/20" : ""}`}>
                      <td className="px-4 py-2 text-slate-400 font-bold font-mono">#{row.id + 2}</td>
                      <td className="px-4 py-2 font-mono">{row.date}</td>
                      <td className="px-4 py-2 font-bold text-slate-700 truncate max-w-[220px]">{row.descricao}</td>
                      <td className="px-4 py-2">
                        {row.categoria_id ? (
                          <span className="text-slate-600 font-semibold">{row.categoria_nome}</span>
                        ) : row.categoria_nome ? (
                          <span className="text-amber-600 bg-amber-50 border border-amber-200/50 px-1.5 py-0.5 rounded text-[10px] font-bold">
                            Criará no staging ({row.categoria_nome})
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">Nenhuma</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {row.centro_id ? (
                          <span className="text-slate-600 font-semibold">{row.centro_nome}</span>
                        ) : row.centro_nome ? (
                          <span className="text-amber-600 bg-amber-50 border border-amber-200/50 px-1.5 py-0.5 rounded text-[10px] font-bold">
                            Sem Centro ({row.centro_nome})
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">Nenhum</span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono font-bold">
                        <span className={row.valor_centavos >= 0 ? "text-emerald-700" : "text-rose-600"}>
                          {formatCentavos(row.valor_centavos)}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {row.hasError ? (
                          <span className="flex items-center gap-1 text-red-600 font-bold text-[10px] uppercase">
                            <AlertCircle size={14} /> Erro de Dados
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-emerald-600 font-bold text-[10px] uppercase">
                            <Check size={14} /> Linha Válida
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rodapé Ações */}
          <div className="flex justify-between items-center bg-slate-100 p-4 rounded-xl border border-slate-200 shrink-0">
            <button
              onClick={() => setStep(1)}
              disabled={loading}
              className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 bg-slate-200 rounded-lg hover:bg-slate-300 transition-colors"
            >
              Voltar e Mudar Arquivo
            </button>

            <button
              onClick={handleIngestImport}
              disabled={loading}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-xs font-bold shadow-md transition-colors"
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" /> Gravando lote...
                </>
              ) : (
                <>
                  Importar {sheetData.length} Linhas <ArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ─── PASSO 3: SUCESSO ───────────────────────────────────────────── */}
      {step === 3 && (
        <div className="max-w-md mx-auto w-full bg-white border border-slate-200 rounded-xl p-8 flex flex-col items-center justify-center text-center shadow-md pt-10">
          <div className="h-16 w-16 bg-emerald-50 border border-emerald-200 text-emerald-600 rounded-full flex items-center justify-center mb-4">
            <Check size={36} />
          </div>
          <h3 className="font-bold text-sm text-slate-800 uppercase tracking-wide">Importação Concluída!</h3>
          <p className="text-xs text-slate-500 mt-2 mb-6">
            O lote de planilha foi processado com sucesso. Todos os registros foram enfileirados como <strong>Lançamentos Pendentes</strong> e estão aguardando conferência humana na aba correspondente.
          </p>

          <button
            onClick={() => setStep(1)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-4 py-2.5 rounded-lg shadow transition-colors w-full"
          >
            Importar Outro Arquivo
          </button>
        </div>
      )}
    </div>
  );
}
